import React, { useState, useEffect } from "react";
import { Bell, Sparkles, ChevronDown, History, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom"; // Added for navigation
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { cn } from "../../lib/utils";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = ({ title }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  const markAllAsRead = async (open) => {
    // We only trigger this when the dropdown opens and there are unread items
    if (open && unreadCount > 0) {
      try {
        await axios.put(`${API_URL}/user/notifications/read-all`);
        // Update local state immediately for a snappy UI
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
      }
    }
  };

  useEffect(() => {
    fetchNotifications();
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/notifications`);
      setNotifications(response.data);
      setUnreadCount(response.data.filter((n) => !n.is_read).length);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  return (
    <header 
      className={cn(
        "sticky top-0 z-50 transition-all duration-500 px-4 py-3",
        scrolled ? "bg-white/80 backdrop-blur-2xl shadow-sm" : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        
        {/* Left Section: User Profile & Greeting */}
        <div className="flex items-center gap-3">
 <div className="relative group cursor-pointer" onClick={() => navigate('/profile')}>
  {/* The Glow Effect */}
  <div className="absolute -inset-1 bg-gradient-to-tr from-slate-900 to-slate-400 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
<div className="relative group cursor-pointer" onClick={() => navigate('/profile')}>
  {/* The Glow Effect */}
  <div className="absolute -inset-1 bg-gradient-to-tr from-slate-900 to-slate-400 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-1000" />
  
  <div className="relative w-10 h-10 rounded-full bg-slate-900 border-2 border-white overflow-hidden shadow-md flex items-center justify-center">
    {user?.profile_picture ? (
      <img 
        src={user.profile_picture} 
        alt="Avatar" 
        className="w-full h-full object-cover transition-opacity duration-300"
        onError={(e) => {
            // If the server image fails (e.g. 404), hide the broken img and show the initial
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
        }}
      />
    ) : null}

    {/* Fallback Initial - Shows if no profile_picture or if image fails to load */}
    <div 
      className={cn(
        "w-full h-full flex items-center justify-center bg-slate-900 text-white font-black text-sm uppercase tracking-tighter",
        user?.profile_picture ? "hidden" : "flex"
      )}
    >
      {user?.first_name?.[0] || 'U'}
    </div>
  </div>
</div>
</div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
               <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 leading-none">
                {scrolled ? title : "Welcome back"}
              </span>
               {!scrolled && <Sparkles size={10} className="text-amber-500 animate-pulse" />}
            </div>
            <h2 className="text-sm font-black text-slate-900 flex items-center gap-1">
              {user?.first_name || "Guest"}
              <ChevronDown size={14} className="text-slate-300" />
            </h2>
          </div>
        </div>

        {/* Right Section: Pill Action Bar */}
        <div className="flex items-center gap-1 bg-white/80 backdrop-blur-md p-1.5 rounded-full border border-slate-100 shadow-sm">
          
          {/* History Button - Replaced Theme Mode Switch */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/contact')}
            className="rounded-full w-9 h-9 hover:bg-slate-900 hover:text-white transition-all duration-300 group"
          >
            <HelpCircle className="h-4 w-4 text-slate-600 group-hover:text-white transition-colors" />
          </Button>

          {/* Separator Line */}
          <div className="w-[1px] h-4 bg-slate-100 mx-0.5" />

          {/* Notifications */}
          <DropdownMenu onOpenChange={markAllAsRead}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative rounded-full w-9 h-9 hover:bg-slate-50 transition-all"
              >
                <Bell className="h-4 w-4 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-2 right-2 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            
            <DropdownMenuContent align="end" className="w-80 rounded-[2rem] p-4 mt-4 border-slate-100 shadow-2xl bg-white/95 backdrop-blur-xl">
              <div className="flex items-center justify-between mb-4 px-2">
                <h3 className="font-black text-slate-900 text-xs uppercase tracking-widest">Recent Alerts</h3>
                <span className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded-full">{unreadCount}</span>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 text-xs font-bold uppercase tracking-tighter">
                    No new activity
                  </div>
                ) : (
                  notifications.map((n) => (
                    <DropdownMenuItem
                      key={n.id}
                      className={cn(
                        "flex flex-col items-start p-3 rounded-2xl cursor-pointer transition-all",
                        !n.is_read ? "bg-slate-50 border-l-4 border-slate-900" : "opacity-60"
                      )}
                    >
                      <span className="font-bold text-[13px] text-slate-900">{n.title}</span>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{n.message}</p>
                    </DropdownMenuItem>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

      </div>
    </header>
  );
};